import React from 'react';

// Captures http(s):// and www. URLs in plain text. Brackets / whitespace
// terminate the match; trailing sentence punctuation is peeled off after
// so the link doesn't swallow it.
const URL_REGEX = /\b(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+)/gi;

const SHORT_LABEL_MAX = 45;

interface LinkifyOptions {
  /** Extra classes applied to every rendered <a>. */
  className?: string;
  /**
   * Show each link as host + path instead of the raw URL, for narrow cards
   * where tracking query strings would otherwise fill several lines. The
   * href and hover tooltip keep the full URL.
   */
  shortenLabels?: boolean;
}

/** `https://www.site.com/a/b?x=1` -> `site.com/a/b`, cut with `…` past the max. */
export const shortUrlLabel = (url: string): string => {
  const label = url
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/[?#].*$/, '')
    .replace(/\/$/, '');
  return label.length > SHORT_LABEL_MAX ? `${label.slice(0, SHORT_LABEL_MAX - 1)}…` : label;
};

/**
 * Splits `text` into a node array where any URL substrings are rendered
 * as anchor tags. Useful for chat/SMS bubbles where the raw text may
 * include links the user wants to follow.
 */
export const linkify = (text: string, options: LinkifyOptions = {}): React.ReactNode[] => {
  if (!text) return [text];
  const { className, shortenLabels } = options;
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  URL_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    let raw = match[0];
    let trailing = '';
    while (raw.length > 0 && /[.,;:!?)]$/.test(raw)) {
      trailing = raw.slice(-1) + trailing;
      raw = raw.slice(0, -1);
    }
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const label = shortenLabels ? shortUrlLabel(raw) : raw;
    out.push(
      <a
        key={key++}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={label !== raw ? href : undefined}
        style={{ color: 'currentColor' }}
        className={`font-semibold underline underline-offset-2 hover:opacity-90${className ? ` ${className}` : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {label}
      </a>,
    );
    if (trailing) out.push(trailing);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
};
